import * as psl from 'psl';

export type DomainMode =
  | 'user_has_existing_website_domain'
  | 'user_wants_website_domain';

export type ValidationPhase = 'typing' | 'blur' | 'submit';

export type DomainValidationErrorKey =
  | 'URL must start with http:// or https://.'
  | 'Invalid URL format.'
  | 'Public domain name must include a TLD (e.g., example.com).'
  | 'TLD must be at least 2 characters long (e.g., .com, .org).'
  | 'TLD is not valid.'
  | 'Whitespace is not allowed in URL/domain.';

export interface DomainValidationResult {
  isValid: boolean;
  isComplete: boolean;
  errorKey?: DomainValidationErrorKey;
  reason?: string;
  normalizedForSubmit?: string;
}

const PROTOCOL_RE = /^([a-z]+):\/\//i;
const IPV4_RE = /^(?:\d{1,3}\.){3}\d{1,3}$/;

export const INVALID_URL_FORMAT_ERROR_KEY =
  'Invalid URL format.' as DomainValidationErrorKey;

export function isInvalidUrlFormatError(
  errorKey?: string
): errorKey is typeof INVALID_URL_FORMAT_ERROR_KEY {
  return errorKey === INVALID_URL_FORMAT_ERROR_KEY;
}

export const TLD_IS_NOT_VALID_ERROR_KEY =
  'TLD is not valid.' as DomainValidationErrorKey;

export function isTldNotValidError(
  errorKey?: string
): errorKey is typeof TLD_IS_NOT_VALID_ERROR_KEY {
  return errorKey === TLD_IS_NOT_VALID_ERROR_KEY;
}

export function shouldDeferValidationErrorWhileTyping(
  errorKey: string | undefined,
  phase: ValidationPhase
): boolean {
  return (
    phase === 'typing' &&
    (isInvalidUrlFormatError(errorKey) || isTldNotValidError(errorKey))
  );
}

function deferInvalidUrlFormatIfTyping(
  phase: ValidationPhase
): DomainValidationResult | null {
  if (phase === 'typing') {
    return {
      isValid: true,
      isComplete: false
    };
  }
  return null;
}

function rejectInvalidUrlFormat(
  phase: ValidationPhase,
  isComplete: boolean,
  reason: string
): {
  isValid: boolean;
  isComplete: boolean;
  errorKey?: DomainValidationErrorKey;
  reason?: string;
} {
  const deferred = deferInvalidUrlFormatIfTyping(phase);
  if (deferred) {
    return { ...deferred, reason };
  }
  return {
    isValid: false,
    isComplete,
    errorKey: INVALID_URL_FORMAT_ERROR_KEY,
    reason
  };
}

function rejectInvalidTld(
  phase: ValidationPhase,
  isComplete: boolean,
  reason: string
): {
  isValid: boolean;
  isComplete: boolean;
  errorKey?: DomainValidationErrorKey;
  reason?: string;
} {
  const deferred = deferInvalidUrlFormatIfTyping(phase);
  if (deferred) {
    return { ...deferred, reason };
  }
  return {
    isValid: false,
    isComplete,
    errorKey: TLD_IS_NOT_VALID_ERROR_KEY,
    reason
  };
}

function parseHostnameInput(rawValue: string): string {
  return rawValue.split(/[/?#]/)[0];
}

function isPotentialHostname(rawValue: string): boolean {
  return /^[a-z0-9.-]+$/i.test(rawValue);
}

function isIncompleteProtocolOnly(rawValue: string): boolean {
  return /^https?:\/\/?$/i.test(rawValue);
}

function validateHostname(
  hostname: string,
  phase: ValidationPhase
): { isValid: boolean; isComplete: boolean; errorKey?: DomainValidationErrorKey; reason?: string } {
  if (!hostname) {
    return rejectInvalidUrlFormat(phase, false, 'missing-hostname');
  }

  if (/\s/.test(hostname)) {
    return {
      isValid: false,
      isComplete: true,
      errorKey: 'Whitespace is not allowed in URL/domain.',
      reason: 'hostname-whitespace'
    };
  }

  const hostnameLc = hostname.toLowerCase();

  if (hostnameLc === 'localhost' || IPV4_RE.test(hostnameLc) || hostnameLc.includes(':')) {
    return rejectInvalidUrlFormat(phase, true, 'localhost-or-ip');
  }

  if (
    hostname.startsWith('.') ||
    hostname.endsWith('.') ||
    hostname.includes('..')
  ) {
    return rejectInvalidUrlFormat(phase, true, 'dot-placement');
  }

  const labels = hostname.split('.');
  if (labels.some((label) => !label)) {
    return rejectInvalidUrlFormat(phase, true, 'empty-label');
  }

  const hasIllegalLabel = labels.some((label) => {
    if (!/^[a-z0-9-]+$/i.test(label)) {
      return true;
    }

    if (label.startsWith('-') || label.endsWith('-')) {
      return true;
    }

    return false;
  });

  if (hasIllegalLabel) {
    return rejectInvalidUrlFormat(phase, true, 'illegal-label');
  }

  if (labels.length < 2) {
    return {
      isValid: phase === 'typing',
      isComplete: false,
      errorKey:
        phase === 'typing'
          ? undefined
          : 'Public domain name must include a TLD (e.g., example.com).',
      reason: 'missing-tld'
    };
  }

  const tld = labels[labels.length - 1];
  const mainDomainLabel = labels[labels.length - 2];

  if (tld.length < 2) {
    return {
      isValid: phase === 'typing',
      isComplete: false,
      errorKey:
        phase === 'typing'
          ? undefined
          : 'TLD must be at least 2 characters long (e.g., .com, .org).',
      reason: 'short-tld'
    };
  }

  if (!/^[a-z]+$/i.test(tld)) {
    return rejectInvalidUrlFormat(phase, true, 'non-alpha-tld');
  }

  if (!mainDomainLabel || mainDomainLabel.toLowerCase() === 'www') {
    return rejectInvalidUrlFormat(phase, true, 'missing-main-domain');
  }

  if (!psl.isValid(hostnameLc)) {
    return rejectInvalidTld(phase, true, 'psl-invalid');
  }

  return {
    isValid: true,
    isComplete: true
  };
}

export function validateDomainOrUrl(params: {
  value?: string;
  mode: DomainMode;
  phase: ValidationPhase;
}): DomainValidationResult {
  const rawValue = params.value || '';
  const value = rawValue.trim();

  if (!value) {
    return {
      isValid: true,
      isComplete: false
    };
  }

  if (/\s/.test(value)) {
    return {
      isValid: false,
      isComplete: true,
      errorKey: 'Whitespace is not allowed in URL/domain.',
      reason: 'value-whitespace'
    };
  }

  const protocolMatch = value.match(PROTOCOL_RE);
  const hasProtocol = Boolean(protocolMatch);
  const protocol = protocolMatch?.[1]?.toLowerCase();

  if (hasProtocol && protocol !== 'http' && protocol !== 'https') {
    return {
      isValid: false,
      isComplete: true,
      errorKey: 'URL must start with http:// or https://.',
      reason: 'unsupported-protocol'
    };
  }

  if (params.mode === 'user_has_existing_website_domain' && !hasProtocol) {
    if (params.phase === 'typing' && isPotentialHostname(value)) {
      return {
        isValid: true,
        isComplete: false
      };
    }

    return {
      isValid: false,
      isComplete: false,
      errorKey: 'URL must start with http:// or https://.',
      reason: 'missing-required-protocol'
    };
  }

  if (hasProtocol) {
    if (params.phase === 'typing' && isIncompleteProtocolOnly(value)) {
      return {
        isValid: true,
        isComplete: false
      };
    }

    let parsedUrl: URL;
    try {
      parsedUrl = new URL(value);
    } catch (error) {
      const deferred = deferInvalidUrlFormatIfTyping(params.phase);
      if (deferred) {
        return { ...deferred, reason: 'url-parse-failed' };
      }
      return {
        isValid: false,
        isComplete: false,
        errorKey: INVALID_URL_FORMAT_ERROR_KEY,
        reason: 'url-parse-failed'
      };
    }

    if (
      parsedUrl.pathname !== '/' ||
      parsedUrl.search !== '' ||
      parsedUrl.hash !== '' ||
      parsedUrl.username !== '' ||
      parsedUrl.password !== ''
    ) {
      const deferred = deferInvalidUrlFormatIfTyping(params.phase);
      if (deferred) {
        return { ...deferred, reason: 'unsupported-url-parts' };
      }
      return {
        isValid: false,
        isComplete: true,
        errorKey: INVALID_URL_FORMAT_ERROR_KEY,
        reason: 'unsupported-url-parts'
      };
    }

    const hostnameResult = validateHostname(parsedUrl.hostname, params.phase);
    if (!hostnameResult.isValid) {
      return {
        isValid: false,
        isComplete: hostnameResult.isComplete,
        errorKey: hostnameResult.errorKey,
        reason: hostnameResult.reason
      };
    }

    if (!hostnameResult.isComplete) {
      return {
        isValid: true,
        isComplete: false
      };
    }

    return {
      isValid: true,
      isComplete: true
    };
  }

  const hostname = parseHostnameInput(value);
  if (hostname !== value) {
    const deferred = deferInvalidUrlFormatIfTyping(params.phase);
    if (deferred) {
      return { ...deferred, reason: 'path-without-protocol' };
    }
    return {
      isValid: false,
      isComplete: true,
      errorKey: INVALID_URL_FORMAT_ERROR_KEY,
      reason: 'path-without-protocol'
    };
  }

  const hostnameResult = validateHostname(hostname, params.phase);
  if (!hostnameResult.isValid) {
    return {
      isValid: false,
      isComplete: hostnameResult.isComplete,
      errorKey: hostnameResult.errorKey,
      reason: hostnameResult.reason
    };
  }

  if (!hostnameResult.isComplete) {
    return {
      isValid: true,
      isComplete: false
    };
  }

  return {
    isValid: true,
    isComplete: true
  };
}

export function normalizeWebsiteForSubmission(params: {
  value?: string;
  mode: DomainMode;
}): DomainValidationResult {
  const validation = validateDomainOrUrl({
    value: params.value,
    mode: params.mode,
    phase: 'submit'
  });

  if (!validation.isValid || !validation.isComplete) {
    return validation;
  }

  const trimmed = (params.value || '').trim().toLowerCase();
  const hasProtocol = /^https?:\/\//i.test(trimmed);

  if (params.mode === 'user_wants_website_domain' && !hasProtocol) {
    return {
      ...validation,
      normalizedForSubmit: `https://${trimmed}`
    };
  }

  return {
    ...validation,
    normalizedForSubmit: trimmed
  };
}
